import type { AssessmentData, SelectableItem } from '@physitrack/championhealth-types';

import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Style.css';

import { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { ValidationContext } from '../../validation/ValidationContext';

type MskBodyMarker = SelectableItem & { top: string; left: string };

type MskBodyProps = {
  questionid: string;
  sex: string;
  onSetValue: (payload: {
    id: string;
    value: AssessmentData[keyof AssessmentData] | null | '';
  }) => void;
  areas_of_pain: SelectableItem[];
};

type BodyMarkerProps = {
  item: MskBodyMarker;
  active: string;
  onClick: () => void;
};

function BodyMarker({ item, active, onClick }: BodyMarkerProps) {
  return (
    <button
      type="button"
      className={`body_marker ${item.id} ${active}`}
      style={{ top: item.top, left: item.left }}
      title={item.title}
      aria-label={item.title}
      aria-pressed={active === 'active'}
      onClick={onClick}
    />
  );
}

type CheckboxItemProps = {
  item: MskBodyMarker;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};

function CheckboxItem({ item, checked, onChange }: CheckboxItemProps) {
  return (
    <div className="checkbox_item">
      <input
        type="checkbox"
        id={item.id}
        name={item.id}
        className="fdatacollect"
        onChange={onChange}
        title={item.title}
        checked={checked}
        aria-label={item.title}
      />
      <label aria-hidden="true" htmlFor={item.id} id={`${item.id}_label`}>
        <FontAwesomeIcon icon={faCheckCircle} className="icon" />
        {item.title}
      </label>
    </div>
  );
}

function withoutUnderscorePrefix(item) {
  if (item.id.startsWith('_')) {
    return { ...item, id: item.id.substring(1) };
  }
  return item;
}

export function MskBody({ questionid, sex, onSetValue, areas_of_pain }: MskBodyProps) {
  const { t } = useTranslation();
  const [areasOfPainFront, setAreasOfPainFront] = useState([]);
  const [areasOfPainBack, setAreasOfPainBack] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isError, setIsError] = useState(false);
  const { registerValidation } = useContext(ValidationContext);

  useEffect(() => {
    if (areasOfPainFront.length === 0) {
      let front = [];
      let back = [];

      if (sex !== 'male') {
        front = [
          { id: '_jaw', title: t('MSK001'), top: '50px', left: '130px' },
          { id: '_pelvis', title: t('MSK002'), top: '215px', left: '140px' },
          { id: '_hip', title: t('MSK003'), top: '215px', left: '65px' },
          { id: '_knee', title: t('MSK004'), top: '355px', left: '95px' },
          { id: '_ankle', title: t('MSK005'), top: '460px', left: '125px' },
          { id: '_foot', title: t('MSK006'), top: '485px', left: '95px' },
          { id: '_shoulder', title: t('MSK007'), top: '90px', left: '55px' },
          { id: '_elbow', title: t('MSK008'), top: '170px', left: '45px' },
          { id: '_wrist', title: t('MSK009'), top: '220px', left: '195px' },
          { id: '_fingers', title: t('MSK010'), top: '265px', left: '220px' },
        ];

        back = [
          { id: '_neck', title: t('MSK011'), top: '70px', left: '110px' },
          { id: '_mid-back', title: t('MSK012'), top: '140px', left: '110px' },
          {
            id: '_lower-back',
            title: t('MSK013'),
            top: '200px',
            left: '110px',
          },
        ];
      } else {
        front = [
          { id: '_jaw', title: t('MSK001'), top: '50px', left: '130px' },
          { id: '_pelvis', title: t('MSK002'), top: '230px', left: '150px' },
          { id: '_hip', title: t('MSK003'), top: '230px', left: '75px' },
          { id: '_knee', title: t('MSK004'), top: '355px', left: '95px' },
          { id: '_ankle', title: t('MSK005'), top: '460px', left: '130px' },
          { id: '_foot', title: t('MSK006'), top: '485px', left: '95px' },
          { id: '_shoulder', title: t('MSK007'), top: '90px', left: '55px' },
          { id: '_elbow', title: t('MSK008'), top: '170px', left: '45px' },
          { id: '_wrist', title: t('MSK009'), top: '230px', left: '205px' },
          { id: '_fingers', title: t('MSK010'), top: '275px', left: '220px' },
        ];

        back = [
          { id: '_neck', title: t('MSK011'), top: '70px', left: '115px' },
          { id: '_mid-back', title: t('MSK012'), top: '155px', left: '115px' },
          {
            id: '_lower-back',
            title: t('MSK013'),
            top: '215px',
            left: '115px',
          },
        ];
      }
      setAreasOfPainFront(front);
      setAreasOfPainBack(back);
    }
  }, [sex, areasOfPainFront.length, t]);

  const addSelectedItemHandler = (event) => {
    const { id } = event.target;
    const { title } = event.target;
    updateBodyMap(id, title);
  };

  const markerClickHandler = (item: MskBodyMarker, active: string) => {
    updateBodyMap(item.id, item.title);
    if (active === 'active') {
      (document.getElementById(item.id) as HTMLInputElement).checked = true;
    } else {
      (document.getElementById(item.id) as HTMLInputElement).checked = false;
    }
  };

  function updateBodyMap(item_id, item_title) {
    // markers and checkboxes use '_'-prefixed DOM ids; selected items store the id without the prefix
    const normalizedId = item_id.startsWith('_') ? item_id.substring(1) : item_id;
    const list = [...selectedItems];
    const existingIndex = list.findIndex((e) => e.id === normalizedId);
    if (existingIndex === -1) {
      list.push({ id: normalizedId, title: item_title });
    } else {
      list.splice(existingIndex, 1);
    }
    setSelectedItems(list);
    saveValue(list);
    setTimeout(() => {
      const element = document.getElementById(item_id);
      if (element) {
        element.focus();
      }
    }, 0);
  }

  const saveValue = useCallback(
    (list) => {
      const array = list.map(withoutUnderscorePrefix);
      onSetValue({ id: questionid, value: array });

      // if one item then set as worst area of pain
      if (array.length === 1) {
        onSetValue({
          id: 'worst_area_of_pain',
          value: { id: array[0].id, title: list[0].title },
        });
      } else {
        onSetValue({ id: 'worst_area_of_pain', value: '' });
      }
    },
    [onSetValue, questionid]
  );

  useEffect(() => {
    registerValidation(() => {
      const valid = selectedItems.length > 0;
      setIsError(!valid);
      return valid;
    });

    if (selectedItems.length > 0) {
      setIsError(false);
    }
  }, [selectedItems, registerValidation]);

  useEffect(() => {
    if (areas_of_pain && selectedItems.length === 0) {
      const array = areas_of_pain.map(withoutUnderscorePrefix);
      setSelectedItems(array);
      saveValue(array);
    }
  }, [areas_of_pain, questionid, selectedItems.length, saveValue]);

  return (
    <div className="req question_block_wide fs-exclude">
      <div className="answer_block_container">
        <div className="answer_block msk_container" id="msk_bg">
          <div className={`msk_body ${sex} front`}>
            {isError && <p className="srOnly">{t('assessment.error.inline.info')}</p>}
            <div className="front" style={{ display: 'inline-block' }}>
              {areasOfPainFront.map((option) => {
                const active = selectedItems.find(
                  (element) => `_${element.id}` === option.id || element.id === option.id
                )
                  ? 'active'
                  : '';

                return (
                  <BodyMarker
                    key={option.id}
                    item={option}
                    active={active}
                    onClick={() => markerClickHandler(option, active)}
                  />
                );
              })}
            </div>
          </div>
          <div className={`msk_body ${sex} back`}>
            <div className="back">
              {areasOfPainBack.map((option) => {
                const active = selectedItems.find(
                  (element) => `_${element.id}` === option.id || element.id === option.id
                )
                  ? 'active'
                  : '';
                return (
                  <BodyMarker
                    key={option.id}
                    item={option}
                    active={active}
                    onClick={() => markerClickHandler(option, active)}
                  />
                );
              })}
            </div>
          </div>
          <fieldset
            id={questionid}
            className="msk_options front checkbox fs-exclude"
            style={{ display: 'inline-block' }}
            data-input-type="msk_checkbox"
          >
            <legend id={`${questionid}-legend`} className="srOnly">
              {t('assessment.bodymap.legend')}
            </legend>

            {areasOfPainFront.map((option) => {
              const checked = selectedItems.find(
                (element) => `_${element.id}` === option.id || element.id === option.id
              )
                ? 'checked'
                : '';
              return (
                <CheckboxItem
                  key={option.id}
                  item={option}
                  checked={checked === 'checked'}
                  onChange={addSelectedItemHandler}
                />
              );
            })}
            {areasOfPainBack.map((option) => {
              const checked = selectedItems.find(
                (element) => `_${element.id}` === option.id || element.id === option.id
              )
                ? 'checked'
                : '';
              return (
                <CheckboxItem
                  key={option.id}
                  item={option}
                  checked={checked === 'checked'}
                  onChange={addSelectedItemHandler}
                />
              );
            })}
            <Alert
              id={`${questionid}-error`}
              isVisible={isError}
              message={t('assessment.error.inline.info.many')}
              type="error"
              isRoleAlert
            />
          </fieldset>
        </div>
      </div>
    </div>
  );
}
